import { Store, AnyAction } from 'redux';
import State from 'store/State';
import { WidgetsService } from 'widgets/WidgetsService';
import { PortfolioInput } from './PortfolioInput';
import {
    selectPortfolioSelection,
    selectPortfolioState,
} from './store/selectors';
import { PositionEntryPortfolioInput } from 'positionEntry/PositionEntryPortfolioInput';
import {
    PortfolioItem,
    defaultPortfolioItemColumns,
    PortfolioItemColumn,
} from './PortfolioItem';
import Order from 'communication/types/order/Order';
import { dispatchSetPortfolioSelectionIfChanged } from './store/dispatcher';
import { portfolioSetSelection } from 'portfolio/store/actions';
import { buildPortfolioSelection } from 'portfolio/PortfolioSelection';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { EmbeddedAppPortfolioInput } from 'embedded/EmbeddedAppPortfolioInput';
import { IndigoDispatch } from 'store/store';

export interface PortfolioServiceOptions {
    widgetsService: WidgetsService;
}

/*
 * Abstracts the current portfolio
 */
export class PortfolioService {
    private widgetsService: WidgetsService;
    public store: Store<State, AnyAction> | undefined;

    public constructor(options: PortfolioServiceOptions) {
        this.widgetsService = options.widgetsService;
    }

    /*
     * Clears the current Portfolio selection
     */
    public clearSelection(): void {
        const state = this.store!.getState();
        const input = this.selectPortfolioActiveInput(state);
        const currentSelection = selectPortfolioSelection(state);
        const { symbology } = selectAppSettings(state);
        if (currentSelection?.overridingActiveInput) {
            this.store?.dispatch(
                portfolioSetSelection(buildPortfolioSelection({ symbology }))
            );
        } else {
            if (input && input.getClearSelectionAction) {
                this.store!.dispatch(input.getClearSelectionAction());
            }
        }
    }

    public dispatchCurrentInputSelection(): void {
        const state = this.store!.getState();
        const input = this.selectPortfolioActiveInput(state);
        if (input) {
            const portfolioSelection = input.getSelection(state);
            dispatchSetPortfolioSelectionIfChanged(
                state,
                this.store!.dispatch,
                portfolioSelection
            );
        }
    }

    /*
     * Gets the complete filtered portfolio (if the current input supports a filtered portfolio)
     */
    public getFilteredPortfolio(): PortfolioItem[] {
        if (!this.store) {
            return [];
        }
        const state = this.store!.getState();
        const input = this.selectPortfolioActiveInput(state);
        return input?.getFilteredPortfolio(state) ?? [];
    }

    /*
     * Gets the complete filtered orders (if the current input uses the orders stream)
     */
    public getFilteredOrders(): Order[] {
        if (!this.store) {
            return [];
        }
        const state = this.store!.getState();
        const input = this.selectPortfolioActiveInput(state);
        return input?.getFilteredOrders(state) ?? [];
    }

    /*
     * Returns True if the active input uses the orders stream
     * The ordersStreamingService only starts the orders stream if this is the case
     * The Blotter and Newport requires this, position entry doesn't
     */
    public get activeInputUsesOrdersStream(): boolean {
        if (!this.store) {
            return false;
        }
        const state = this.store.getState();
        return !!this.selectPortfolioActiveInput(state)?.usesOrdersStream(
            state
        );
    }

    public get activeInputOverridesWidget(): boolean {
        if (!this.store) {
            return false;
        }
        const state = this.store.getState();
        const portfolioState = selectPortfolioState(state);
        return portfolioState.activeInputType === 'Position Entry';
    }

    public getActiveInputStreamingOrderIds(): string[] {
        if (!this.store) {
            return [];
        }
        const state = this.store.getState();
        const getStreamingOrderIds =
            this.selectPortfolioActiveInput(state)?.getStreamingOrderIds;
        return (getStreamingOrderIds && getStreamingOrderIds(state)) ?? [];
    }

    public getPortfolioColumns(): PortfolioItemColumn[] {
        if (!this.store) {
            return defaultPortfolioItemColumns;
        }
        const state = this.store.getState();
        if (state === undefined) {
            return defaultPortfolioItemColumns;
        }
        const activeInput = this.selectPortfolioActiveInput(state);
        return activeInput
            ? activeInput.getPortfolioColumns(state)
            : defaultPortfolioItemColumns;
    }

    public isColumnInPortfolio(column: PortfolioItemColumn): boolean {
        const cols = this.getPortfolioColumns();
        return cols.indexOf(column) > -1;
    }

    public selectPortfolioActiveInput(
        state: State
    ): PortfolioInput | undefined {
        const portfolioState = selectPortfolioState(state);
        switch (portfolioState.activeInputType) {
            case 'Embedded App': {
                return EmbeddedAppPortfolioInput;
            }
            case 'Position Entry': {
                return PositionEntryPortfolioInput;
            }
            case 'Widget': {
                return this.widgetsService.getPortfolioInput(
                    portfolioState.activeInputWidget!
                )!;
            }
            default: {
                return undefined;
            }
        }
    }

    public updateActiveFilter(
        fieldName: string,
        values: string[],
        exclude: boolean
    ) {
        const state = this.store!.getState();
        const activeInput = this.selectPortfolioActiveInput(state)!;
        if (!activeInput.updateActiveFilter) {
            throw new Error('Active input does not support filtering');
        }
        activeInput.updateActiveFilter(
            state,
            this.store!.dispatch as IndigoDispatch,
            fieldName,
            values,
            exclude
        );
    }
}
